/***********************************
 *  Grade1EnglishLesson.js
 *  Chapter-based structure
 ***********************************/

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Grade1EnglishLesson.css";
import { awardToken } from "../../awardtoken.js";
import PhonicsMatchingGame from "../EnglishTool/PhonicsMatchingGame.js";
import { chapterPages } from "./Grade1chapterPage.js";
import SentenceMakingGame from "../EnglishTool/SentenceMakingGame.js";
import SpeakButton from "../EnglishTool/SpeakButton.js";

const allLetters = [
  "a", "b", "c", "d", "e", "f", "g",
  "h", "i", "j", "k", "l", "m", "n",
  "o", "p", "q", "r", "s", "t", "u",
  "v", "w", "x", "y", "z"
];

/**
 * =============== 2) QUESTION POOLS ===============
 * (From your original code)
 */
const phonicsPool = [
  { word: "cat", correct: "c" }, { word: "dog", correct: "d" },
  { word: "sun", correct: "s" }, { word: "fish", correct: "f" },
  { word: "apple", correct: "a" }, { word: "bat", correct: "b" },
  { word: "hat", correct: "h" }, { word: "milk", correct: "m" },
  { word: "lion", correct: "l" }, { word: "queen", correct: "q" },
  { word: "zebra", correct: "z" }, { word: "goat", correct: "g" },
  { word: "key", correct: "k" }, { word: "map", correct: "m" },
  { word: "nose", correct: "n" }, { word: "egg", correct: "e" },
  { word: "toy", correct: "t" }, { word: "cake", correct: "c" },
  { word: "pizza", correct: "p" }, { word: "duck", correct: "d" },
  { word: "orange", correct: "o" }, { word: "house", correct: "h" },
  { word: "jelly", correct: "j" }, { word: "bike", correct: "b" },
  { word: "yellow", correct: "y" },
];

const sightWordsPool = [
  "the", "and", "is", "it", "can", "see", "my", "me", "go", "up",
  "in", "we", "to", "for", "at", "come", "of", "you", "that", "are",
  "was", "they", "said", "he", "she"
];

const sentencePool = [
  // needs a noun
  { sentence: "I love my ___", correct: "dog", requiredPOS: "noun" },
  // needs a verb
  { sentence: "She likes to ___", correct: "run", requiredPOS: "verb" },
  // needs an adjective
  { sentence: "The sun is very ___", correct: "bright", requiredPOS: "adjective" },
  // needs a verb
  { sentence: "He will ___ a letter", correct: "write", requiredPOS: "verb" },
  // “together” is effectively an adverb here
  { sentence: "We can play ___", correct: "together", requiredPOS: "adverb" },
  // needs a noun
  { sentence: "I have a red ___", correct: "ball", requiredPOS: "noun" },
  { sentence: "They saw a big ___", correct: "tree", requiredPOS: "noun" },
  { sentence: "Please read this ___", correct: "book", requiredPOS: "noun" },
  // needs a verb
  { sentence: "I want to ___ cookies", correct: "eat", requiredPOS: "verb" },
  { sentence: "She is ___ a story", correct: "telling", requiredPOS: "verb" },
  { sentence: "My cat likes to ___", correct: "sleep", requiredPOS: "verb" },
  { sentence: "We ___ to school", correct: "walk", requiredPOS: "verb" },
  // needs a noun
  { sentence: "You have a nice ___", correct: "smile", requiredPOS: "noun" },
  { sentence: "He drank some ___", correct: "water", requiredPOS: "noun" },
  // needs a verb (past tense, but you can still just call it “verb”)
  { sentence: "The bird ___ away", correct: "flew", requiredPOS: "verb" },
  { sentence: "My friend and I ___", correct: "played", requiredPOS: "verb" },
  // needs an adjective
  { sentence: "We saw a ___ rainbow", correct: "huge", requiredPOS: "adjective" },
  { sentence: "Mom cooked my ___ meal", correct: "favorite", requiredPOS: "adjective" },
  { sentence: "Dad said ___ morning", correct: "good", requiredPOS: "adjective" },
  // “which” is often called a determiner or interrogative adjective
  { sentence: "___ day is today?", correct: "Which", requiredPOS: "determiner" },
  // needs a verb
  { sentence: "I ___ going to the park", correct: "am", requiredPOS: "verb" },
  { sentence: "Do you ___ ice cream?", correct: "like", requiredPOS: "verb" },
  { sentence: "___ me your picture", correct: "Show", requiredPOS: "verb" },
  // “drink” is used here as a noun (“Take a drink”)
  { sentence: "Take a ___ of water", correct: "drink", requiredPOS: "noun" },
  { sentence: "The ___ is shining bright", correct: "moon", requiredPOS: "noun" },
];


/** 
 * We will treat:
 *    chapterTopicMap => {1: "phonics", 2: "sightWords", 3: "sentences"} 
 * Then a final quiz from all 3 combined => 75 total => pick 50
 */
const chapterTopicMap = {
  1: "phonics",
  2: "sightWords",
  3: "sentenceBuild",
  4: "grammarBasics"
};

/**
 * Utility: shuffle array
 */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * ============ 4) Question Generators ============
 */
function generateOneQuestion(forcedTopic = null) {
  // The possible topics (including your new grammarBasics)
  const topics = ["phonics", "sightWords", "sentenceBuild", "grammarBasics"];
  const topic = forcedTopic || topics[Math.floor(Math.random() * topics.length)];

  if (topic === "phonics") {
    const item = phonicsPool[Math.floor(Math.random() * phonicsPool.length)];
    const questionText = `Which letter does “${item.word}” start with?`;
    const correctAnswer = item.correct;

    // random letters
    let randomLetters = shuffleArray(allLetters).slice(0, 7);
    randomLetters = randomLetters.filter(ch => ch !== correctAnswer);
    randomLetters.push(correctAnswer);
    const options = shuffleArray(randomLetters);

    return {
      question: questionText,
      correctAnswer,
      options,
      ttsWord: item.word, // Optionally let them "Speak" the word
    };
  } 
  
  else if (topic === "sightWords") {
    // Pick one sight word from the pool as correct
    const correct = sightWordsPool[
      Math.floor(Math.random() * sightWordsPool.length)
    ];
    const questionText = "Which of these is a real sight word?";

    // Some distractors
    const distractors = ["zz", "xx", "abc", "thh", "saiid", "mee"];
    let chosen = shuffleArray(distractors).slice(0, 3);
    chosen.push(correct);

    const options = shuffleArray(chosen);

    return {
      question: questionText,
      correctAnswer: correct,
      options,
      ttsWord: correct,
    };
  } 
  
  else if (topic === "sentenceBuild") {
    const item = sentencePool[Math.floor(Math.random() * sentencePool.length)];
    const { sentence, correct, requiredPOS } = item;
  
    const questionText = `Fill in the blank: ${sentence}`;
    // Use the correct part of speech to decide which distractor list
    let possibleDistractors;
    // Example distractors by part of speech:
const nounDistractors = [
  "cat", "dog", "ball", "tree", "house", "cookie", "car", "book"
];
const verbDistractors = [
  "run", "jump", "sleep", "walk", "dance", "eat", "sing"
];
const adjDistractors = [
  "big", "small", "blue", "funny", "fast", "dark", "cold"
];

// If you like a fallback “miscellaneous” set:
const someGeneralDistractors = [
  "pizza", "think", "nice", "purple", "laugh", "school", "computer"
];

    switch(requiredPOS) {
      case "verb":
        possibleDistractors = nounDistractors.concat(adjDistractors);
        break;
      case "noun":
        possibleDistractors = verbDistractors.concat(adjDistractors);
        break;
      // etc.
      default:
        possibleDistractors = [...someGeneralDistractors];
    }
  
    // Then pick 3 or 4 items from `possibleDistractors` that aren’t `correct`
    let arr = [correct];
    while (arr.length < 4) {
      const d = possibleDistractors[Math.floor(Math.random() * possibleDistractors.length)];
      if (!arr.includes(d)) arr.push(d);
    }
    const options = shuffleArray(arr);
  
    return {
      question: questionText,
      correctAnswer: correct,
      options
    };
  } else if (topic === "grammarBasics") {
    const grammarQuestions = [
      // 1) Identifying nouns
      {
        question: "Which word is the noun in “I play ball”?",
        correctAnswer: "ball",
        options: ["play", "I", "ball", "both play & ball"]
      },
      {
        question: "Which word is the noun in “The dog runs”?",
        correctAnswer: "dog",
        options: ["dog", "runs", "the", "both dog & runs"]
      },
      {
        question: "Which word is the noun in “They read books”?",
        correctAnswer: "books",
        options: ["They", "read", "books", "both They & books"]
      },
      {
        question: "Which word is the noun in “Birds fly”?",
        correctAnswer: "Birds",
        options: ["Birds", "fly", "the", "both Birds & fly"]
      },
    
      // 2) Identifying verbs
      {
        question: "Which word is the verb in “My cat sleeps”?",
        correctAnswer: "sleeps",
        options: ["cat", "my", "sleeps", "both cat & sleeps"]
      },
      {
        question: "Which word is the verb in “Mom plays piano”?",
        correctAnswer: "plays",
        options: ["Mom", "plays", "piano", "both plays & piano"]
      },
      {
        question: "Which word is the verb in “The cat jumps”?",
        correctAnswer: "jumps",
        options: ["cat", "jumps", "the", "both cat & jumps"]
      },
      {
        question: "Which word is the verb in “He runs fast”?",
        correctAnswer: "runs",
        options: ["He", "runs", "fast", "He & runs"]
      },
    
      // 3) Identifying adjectives
      {
        question: "Which word is the adjective in “The big dog barked”?",
        correctAnswer: "big",
        options: ["The", "big", "dog", "barked"]
      },
      {
        question: "Which word is the adjective in “A red apple”?",
        correctAnswer: "red",
        options: ["A", "red", "apple", "A & apple"]
      },
      {
        question: "Which word is the adjective in “I see a blue car”?",
        correctAnswer: "blue",
        options: ["I", "see", "blue", "car"]
      },
      {
        question: "Which word is the adjective in “I like happy songs”?",
        correctAnswer: "happy",
        options: ["I", "like", "happy", "songs"]
      },
    
      // 4) Identifying adverbs
      {
        question: "Which word is the adverb in “He runs quickly”?",
        correctAnswer: "quickly",
        options: ["He", "runs", "quickly", "He & runs"]
      },
      {
        question: "Which word is the adverb in “They sing loudly”?",
        correctAnswer: "loudly",
        options: ["They", "sing", "loudly", "sing & loudly"]
      },
      {
        question: "Which word is the adverb in “Mom dances gracefully”?",
        correctAnswer: "gracefully",
        options: ["Mom", "dances", "gracefully", "none of these"]
      },
      {
        question: "Which word is the adverb in “She speaks softly”?",
        correctAnswer: "softly",
        options: ["she", "speaks", "softly", "speaks & softly"]
      },
    ];

    
    // Pick a random item from grammarQuestions
    const chosen = grammarQuestions[Math.floor(Math.random() * grammarQuestions.length)];
  
    return {
      question: chosen.question,
      correctAnswer: chosen.correctAnswer,
      options: shuffleArray(chosen.options)
    };
  }

}

/**
 * Generate chapter quiz of n=10 from a single topic
 */
function generateChapterQuiz(n = 10, forcedTopic) {
  const quizArr = [];
  for (let i=0; i<n; i++){
    quizArr.push(generateOneQuestion(forcedTopic));
  }
  return quizArr;
}

/**
 * Build big array of 75 questions => final quiz picks 50
 */
function buildFullQuestionBank() {
  let bigSet = [];

  // 1) Phonics
  phonicsPool.forEach(item => {
    const question = `Which letter does "${item.word}" start with?`;
    const correctAnswer = item.correct;
    const bigLetters = [
      "a","b","c","d","e","f","g","h","i","j","k","l","m","n","o","p",
      "q","r","s","t","u","v","w","x","y","z"
    ];
    let arr = shuffleArray(bigLetters).slice(0,7);
    arr.push(correctAnswer);
    bigSet.push({
      question,
      correctAnswer,
      options: shuffleArray(arr)
    });
  });

  // 2) Sight Words
  sightWordsPool.forEach(sw => {
    const question = "Which of these is a real sight word?";
    const correct = sw;
    const distractors = ["zzz","xyz","abc","andd","loll","qrs","zzis","yuo","ok"];
    let chosen = shuffleArray(distractors).slice(0,3);
    chosen.push(correct);
    bigSet.push({
      question,
      correctAnswer: correct,
      options: shuffleArray(chosen),
    });
  });

  // 3) Sentence-Building
  sentencePool.forEach(item => {
    const question = `Fill in the blank: ${item.sentence}`;
    const correct = item.correct;
    const distractors = [
      "food","sing","dark","tall","blue","cake","zoom","play","kind",
      "day","smile","fast","no","look"
    ];
    let arr = [correct];
    while (arr.length < 5) {
      const d = distractors[Math.floor(Math.random() * distractors.length)];
      if (!arr.includes(d)) arr.push(d);
    }
    bigSet.push({
      question,
      correctAnswer: correct,
      options: shuffleArray(arr)
    });
  });

  // 4) Grammar (chapter 4)
  const grammarQuestions = [
    // Example set. Feel free to expand or adjust.
    {
      question: "Which word is the noun in “I play ball”?",
      correctAnswer: "ball",
      options: ["play","I","ball","both play & ball"]
    },
    {
      question: "Which word is the noun in “The dog runs”?",
      correctAnswer: "dog",
      options: ["dog","runs","the","both dog & runs"]
    },
    {
      question: "Which word is the verb in “The cat jumps”?",
      correctAnswer: "jumps",
      options: ["cat","jumps","the","both cat & jumps"]
    },
    {
      question: "Which word is the adjective in “A red apple”?",
      correctAnswer: "red",
      options: ["A","red","apple","A & apple"]
    },
    {
      question: "Which word is the adverb in “He runs quickly”?",
      correctAnswer: "quickly",
      options: ["He","runs","quickly","He & runs"]
    },
    // ...Add as many grammar Qs as you like...
  ];

  grammarQuestions.forEach(gq => {
    // We can shuffle the options the same way we do above
    bigSet.push({
      question: gq.question,
      correctAnswer: gq.correctAnswer,
      options: shuffleArray(gq.options)
    });
  });

  // Now bigSet includes questions from all 4 chapters
  // e.g. 25 phonics + 25 sight words + 25 sentences + ~10 grammar
  return bigSet;
}


function generateFinalQuiz(count=50) {
  const allQ = buildFullQuestionBank();  // 75 total
  const shuffled = shuffleArray(allQ);
  return shuffled.slice(0, count);
}

/**
 * =============== Main Component ===============
 */
export default function Grade1EnglishLesson({ onAwardTokens = () => {} }) {
  /** A) Chapters, page index, final states, etc. */
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);
  const [chapDone, setChapDone] = useState(false);

  // Chapter quiz
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);

  // Show or hide the "chapter quiz"
  const [showQuiz, setShowQuiz] = useState(false);

  // Mark if user completed before => localStorage
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

  // Final quiz stuff
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 min
  // track awarding tokens once
  const hasAwardedTokens = useRef(false);

  
  //  B) On mount, check localStorage
  useEffect(() => {
    // Attempt to read the stored chapter, page, and done status
    const storedChapter = localStorage.getItem("g1English_chapter");
    const storedPage = localStorage.getItem("g1English_pageIdx");
    const done = localStorage.getItem("grade1EnglishComplete");
    
     // If we have them, parse them and set state
     if (storedChapter) {
      setChapter(parseInt(storedChapter, 10) || 1);
    }
    if (storedPage) {
      setPageIdx(parseInt(storedPage, 10) || 0);
    }
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true);
    }
  }, []);

    // Whenever chapter or pageIdx changes, save to localStorage
    useEffect(() => {
      localStorage.setItem("g1English_chapter", String(chapter));
      localStorage.setItem("g1English_pageIdx", String(pageIdx));
    }, [chapter, pageIdx]);

  // If chapDone => store localStorage
  useEffect(() => {
    if (chapDone) {
      localStorage.setItem("grade1EnglishComplete", "true");
    }
  }, [chapDone]);

  //  C) Timer for final quiz
  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);  // time's up
    }
  }, [showFinal, fFin, timeLeft]);

  //  D) Access the current pages for the chosen chapter
  const currentPages = chapterPages[chapter];
  const isLastPage = pageIdx === currentPages.length - 1;

  // Get how many total pages exist across all chapters
function getTotalPages(chapterData) {
  // chapterData is an object like {1: [ ... ], 2: [ ... ], 3: [ ... ]}
  // We'll sum up the .length of each array of pages
  return Object.values(chapterData).reduce((sum, pages) => sum + pages.length, 0);
}

// Convert the user’s (chapter, pageIdx) into an absolute page number
function getAbsolutePage(chapterData, chapter, pageIdx) {
  // Sum the page lengths for chapters < current chapter
  let pagesBefore = 0;
  for (let c = 1; c < chapter; c++) {
    pagesBefore += chapterData[c].length;
  }
  // Then add the pageIdx (zero-based) plus 1 (so the 0th page is “page 1”)
  return pagesBefore + pageIdx + 1;
}

  const totalPages = getTotalPages(chapterPages);
  const absolutePage = getAbsolutePage(chapterPages, chapter, pageIdx);
  const progress = Math.round( (absolutePage / totalPages) * 100 );

  // -------------- E) CHAPTER QUIZ logic --------------
  function startChapterQuiz() {
    // For chapter 1 => we do a matching game, no generation needed
    // For chapters 2,3 => we do multiple choice
    if (chapter === 1) {
      setShowQuiz(true);
      // no multiple‐choice quiz needed
    } else {
      const topicKey = chapterTopicMap[chapter]; // e.g. "sightWords" or "sentenceBuild"
      const generated = generateChapterQuiz(10, topicKey);
      setQuiz(generated);
      setQIdx(0);
      setQScore(0);
      setQuizFin(false);
      setShowQuiz(true);
    }
  }

  // On each answer
  function handleAnswer(opt) {
    if (opt === quiz[qIdx].correctAnswer) {
      setQScore((s) => s + 1);
    }
    if (qIdx < quiz.length - 1) {
      setQIdx((i) => i + 1);
    } else {
      setQuizFin(true);
    }
  }

  // Once MC quiz is finished for chapters 2 or 3
  function handleMCQuizFinish() {
    if (qScore >= 5) {
      if (chapter < 4) {
        // Move to next chapter
        setChapter((ch) => ch + 1);
        setPageIdx(0);
      } else {
        // That was chapter 4 => all done
        setChapDone(true);
      }
    } else {
      alert("Below 50%. Please review and retake!");
    }
    // Reset quiz states
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(false);
  }

  // Chapter1 matching game finish
  function handleMatchingGameFinish(score, total) {
    // Suppose you need ≥ 50% correct
    if (score / total >= 0.5) {
      // pass
      if (chapter < 4) {
        setChapter((ch) => ch + 1);
        setPageIdx(0);
      } else {
        setChapDone(true);
      }
    } else {
      alert("Below 50%. Please review and retake!");
    }
    setShowQuiz(false);
  }
  

  /** =============== Final Quiz =============== */
  function startFinalQuiz() {
    const final = generateFinalQuiz(50);  // from all 3
    setFinalQs(final);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(300);
    setShowFinal(true);
  }

  function handleFinalAnswer(opt) {
    if (opt === finalQs[fIdx].correctAnswer) {
      setFScore(s => s + 1);
    }
    if (fIdx < finalQs.length - 1) {
      setFIdx(i => i + 1);
    } else {
      setFFin(true);
    }
  }

  // award tokens if >=80
useEffect(() => {
  if (fFin && !hasAwardedTokens.current) {
    const percent = (fScore / finalQs.length) * 100;
    if (percent >= 80) {
      awardToken();
      if (onAwardTokens) onAwardTokens(1);
    }

    // --- Save final quiz result for dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["english_g1"] = {
      ...(quizResults["english_g1"] || {}),
      finalQuizScore: fScore,
      finalQuizTotal: finalQs.length,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // -------------------------------------------

    hasAwardedTokens.current = true;
  }
}, [fFin, fScore, finalQs.length, onAwardTokens]);


  /** =============== Renders for Lesson Page & Quiz =============== */
  function ChapterSlide() {
    const page = currentPages[pageIdx];
    return (
      <div>
        <h2 className="lesson-chapter-title">{page.title}</h2>
        <div className="lesson-chapter-body">{page.body}</div>
        <div style={{ marginTop: 10 }}>
          {pageIdx > 0 && (
            <button className="button1" onClick={() => setPageIdx(p => p - 1)}>Back</button>
          )}
          {!isLastPage && (
            <button className="button1" onClick={() => setPageIdx(p => p + 1)} style={{ marginLeft: 10 }}>
              Next
            </button>
          )}
          {isLastPage && (
            <button className="button1" onClick={startChapterQuiz} style={{ marginLeft: 15 }}>
              Take Chapter {chapter} Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  function ChapterQuiz() {
    if (!quiz[qIdx]) return <p>Loading quiz...</p>;
    const curQ = quiz[qIdx];

    if (!quizFin) {
      return (
        <div className="quiz-panel">
          <p>
            {curQ.question}
            {curQ.ttsWord && (
              <>
                {" "}
                <SpeakButton text={curQ.ttsWord} />
              </>
            )}
          </p>
          {curQ.options.map((opt, i) => (
            <button
              key={i}
              className="button1"
              onClick={() => handleAnswer(opt)}
            >
              {opt}
            </button>
          ))}
          <p>Score: {qScore} / {quiz.length}</p>
        </div>
      );
    } else {
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={handleMCQuizFinish}>
            Continue
          </button>
        </div>
      );
    }
  }
  

function ChapterUI() {
  if (!showQuiz) {
    return <ChapterSlide />;
  } else {
    if (chapter === 1) {
      return <PhonicsMatchingGame onFinish={handleMatchingGameFinish} />;
    } else if (chapter === 2) {
      return <ChapterQuiz />;
    } else if (chapter === 3) {
      // No need to call startSentenceGame, just use sentenceWords!
      return (
        <SentenceMakingGame
  targetSentence="I see a dog"
  onFinish={(score, total) => {
    if (score === total) {
      alert("You built the sentence correctly!");
      setChapter(4);
      setPageIdx(0);
      setShowQuiz(false);
    } else {
      alert("Try again!");
      // 只要重設 SentenceMakingGame 就會自動洗牌
    }
  }}
/>
      );
    } else if (chapter === 4) {
      if (!showQuiz) {
        return <ChapterSlide />;
      } else {
        return <ChapterQuiz />;
      }
    }
  }
}

  function FinalQuiz() {
    // If the final quiz is still in progress:
    if (!fFin) {
      const cQ = finalQs[fIdx];
      return (
        <div className="final-quiz-container">
          <h2>Final Quiz (50 Qs)</h2>
          <p>
            Time: {Math.floor(timeLeft / 60)}:
            {String(timeLeft % 60).padStart(2, "0")}
          </p>
          <p>
            Q {fIdx + 1}/{finalQs.length}: {cQ.question}
          </p>
          <div className="final-quiz-options">
            {cQ.options.map((o, i) => (
              <button
                key={i}
                className="button1"
                onClick={() => handleFinalAnswer(o)}
              >
                {o}
              </button>
            ))}
          </div>
          <p>Score: {fScore}</p>
        </div>
      );
    }
  
    // If the user has finished the final quiz:
    else {
      // The awarding of tokens if score >= 80% is done in a useEffect below.
      const total = finalQs.length;
      const percent = Math.round((fScore / total) * 100);
  
      return (
        <div className="final-quiz-finished">
          <h3>Final Quiz Completed!</h3>
          <p>
            You scored {fScore} / {total} → {percent}% correct
          </p>
          <p>
            {/* 
              If you want to display a quick message about tokens, or
              just say "Well done!"
            */}
            {percent >= 80
              ? "Great job! You’ve earned a token reward!"
              : "Good effort! Keep practicing to earn a reward."}
          </p>
  
          {/* 
            Instead of calling finishChapterQuiz(), we simply hide
            the final quiz UI, so user remains at the lesson page or sees
            the “All done!” message
          */}
          <button className="button1" onClick={() => setShowFinal(false)}>
            All Done!
          </button>
        </div>
      );
    }
  }  

  return (
    <div className="grade1-english-lesson-page">
      <div style={{ margin:"10px 0" }}>
        <div style={{ width:"80%", margin:"0 auto", background:"#ddd", borderRadius:10, height:20 }}>
          <div style={{
            width: `${progress}%`,
            background:"#4caf50",
            height:20,
            transition:"width 0.3s"
          }} />
        </div>
        <p>{progress}% complete</p>
      </div>

      {/* If user previously completed => jump UI */}
      {hasCompletedBefore && !showFinal && !showQuiz && (
  <div style={{ margin:"15px 0", textAlign:"center", color:"#555" }}>
    <p style={{ marginBottom:10 }}>
      You previously completed all Grade 1 English lessons. ...
    </p>
    <div>
      {/* If you want them to be able to truly redo it, 
          setChapDone(false) and also possibly clear localStorage */}
      {[1,2,3,4].map(ch => (
        <button
          className="button1"
          key={ch}
          onClick={() => {
            // “Un-finish” so they can re-do
            setChapDone(false);
            //localStorage.removeItem("grade1EnglishComplete"); 
              // or not, if you only want to reset temporarily

            setChapter(ch);
            setPageIdx(0);
            setShowQuiz(false);
            setShowFinal(false);
          }}
          style={{ marginRight:5 }}
        >
          Go to Chapter {ch}
        </button>
      ))}
      <button className="button1" onClick={startFinalQuiz}>
        Go to Final Quiz
      </button>
    </div>
  </div>
)}

      {/* Normal chapter UI if not all done & not in final quiz */}
      {!chapDone && !showFinal && <ChapterUI />}

      {/* If all chapters done => final quiz */}
      {chapDone && !showFinal && (
        <div style={{ textAlign:"center" }}>
          <h3>All Chapters Completed!</h3>
          <button className="button1" onClick={startFinalQuiz}>
            Start Final Quiz
          </button>
        </div>
      )}

      {/* Final quiz in progress? */}
      {showFinal && <FinalQuiz />}

      <br/>
      <Link to="/english" className="g1e-back-button">
        ← Back to English Page
      </Link>
    </div>
  );
}
